import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import type { AgentSummary } from '../../types/agent-studio';
import { AgentStatusPill } from '../../components/agent-studio';

function AgentCard({ agent }: { agent: AgentSummary }) {
  const navigate = useNavigate();

  return (
    <div
      className="card"
      onClick={() => navigate(`/agents/${agent.system_name}`)}
      style={{
        padding: '20px',
        cursor: 'pointer',
        transition: 'border-color 0.15s ease',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = 'var(--navy)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = '';
      }}
    >
      {/* Top row: system_name (eyebrow) + status pill */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
      }}>
        <div className="t-eyebrow" style={{ color: 'var(--ink-secondary)' }}>
          {agent.system_name}
        </div>
        <AgentStatusPill status={agent.status} />
      </div>

      {/* Display name (large) */}
      <div className="t-display" style={{
        fontSize: '24px',
        marginBottom: '20px',
      }}>
        {agent.display_name}
      </div>

      {/* Metric chips row */}
      <div style={{
        display: 'flex',
        gap: '24px',
        fontSize: '13px',
        color: 'var(--ink-secondary)',
      }}>
        <div>
          <span style={{ color: 'var(--ink-primary)', fontWeight: 600 }}>
            {agent.runs_today}
          </span>
          <span style={{ marginLeft: '4px' }}>запусков</span>
        </div>
        <div>
          <span style={{ color: 'var(--ink-primary)', fontWeight: 600 }}>
            ${agent.cost_today.toFixed(2)}
          </span>
          <span style={{ marginLeft: '4px' }}>стоимость</span>
        </div>
        <div>
          <span style={{
            color: agent.error_count_today > 0
              ? 'var(--red, #C0392B)'
              : 'var(--ink-primary)',
            fontWeight: 600,
          }}>
            {agent.error_count_today}
          </span>
          <span style={{ marginLeft: '4px' }}>ошибок</span>
        </div>
      </div>

      {/* AI-GEOLOGIST-V1: dedicated workspace entry point (standalone agent) */}
      {agent.system_name === 'geologist' && (
        <button
          data-testid="open-workspace"
          onClick={(e) => { e.stopPropagation(); navigate('/agents/geologist/workspace'); }}
          style={{
            marginTop: 18, background: 'var(--navy)', color: '#fff', border: 'none',
            padding: '9px 18px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          Открыть рабочее место
        </button>
      )}
    </div>
  );
}

function LoadingState() {
  return (
    <div
      style={{
        marginTop: '24px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
        gap: '16px',
      }}
    >
      {[0, 1, 2, 3, 4, 5, 6, 7].map(i => (
        <div
          key={i}
          className="card"
          style={{
            padding: '20px',
            opacity: 0.4,
            height: '120px',
          }}
        >
          <div style={{
            background: 'var(--bg-subtle)',
            height: '12px',
            width: '80px',
            borderRadius: '4px',
            marginBottom: '12px',
          }} />
          <div style={{
            background: 'var(--bg-subtle)',
            height: '24px',
            width: '160px',
            borderRadius: '4px',
            marginBottom: '20px',
          }} />
          <div style={{
            background: 'var(--bg-subtle)',
            height: '12px',
            width: '220px',
            borderRadius: '4px',
          }} />
        </div>
      ))}
    </div>
  );
}

function ErrorState({ errorMessage }: { errorMessage: string }) {
  return (
    <div
      className="card"
      style={{
        marginTop: '24px',
        padding: '32px',
        borderLeft: '3px solid var(--red, #C0392B)',
      }}
    >
      <div className="t-eyebrow" style={{ color: 'var(--red, #C0392B)' }}>
        Ошибка загрузки
      </div>
      <p className="t-body" style={{ marginTop: '8px' }}>
        Не удалось загрузить список агентов. Проверьте подключение
        к серверу и обновите страницу.
      </p>
      <p className="t-body-sm" style={{ marginTop: '12px', color: 'var(--ink-secondary)' }}>
        Подробности (для разработчика): {errorMessage}
      </p>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      className="card"
      style={{
        marginTop: '24px',
        padding: '32px',
        textAlign: 'center',
      }}
    >
      <p className="t-body" style={{ color: 'var(--ink-secondary)' }}>
        Агенты не найдены.
      </p>
    </div>
  );
}

function SuccessGrid({ agents }: { agents: AgentSummary[] }) {
  return (
    <div
      style={{
        marginTop: '24px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
        gap: '16px',
      }}
    >
      {agents.map(agent => (
        <AgentCard key={agent.system_name} agent={agent} />
      ))}
    </div>
  );
}

export default function AgentStudioList() {
  const [agents, setAgents] = useState<AgentSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchAgents() {
      try {
        setLoading(true);
        setError(null);
        const response = await api.listAgents();
        if (!cancelled) {
          setAgents(response.agents);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    fetchAgents();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div style={{ padding: '24px' }}>
      {/* Top section (always visible) */}
      <div className="t-eyebrow">Фаза 1 · Студия агентов</div>
      <h1 className="t-display" style={{ marginTop: '8px' }}>
        Студия агентов
      </h1>
      <p className="t-body-sm" style={{
        marginTop: '4px',
        color: 'var(--ink-secondary)',
        maxWidth: '720px',
      }}>
        Реестр агентов системы. Нажмите на агента, чтобы открыть детальную страницу
        и историю его версий.
      </p>

      {/* Conditional state */}
      {loading && <LoadingState />}
      {!loading && error && <ErrorState errorMessage={error} />}
      {!loading && !error && agents && agents.length === 0 && <EmptyState />}
      {!loading && !error && agents && agents.length > 0 && (
        <SuccessGrid agents={agents} />
      )}
    </div>
  );
}
